/**
 * Create/edit drawer for custom roles. Admins compose a role from the same
 * plain-language vocabulary the matrix uses: a visibility level (the §14
 * ladder) and capability checkboxes grouped by business area — never raw
 * verbs. Built-in roles can't be edited (server-enforced too); "duplicate"
 * opens this drawer prefilled with a fresh key.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useCreateBundle, useListDataPacks, useListRoleTemplates, useSaveAsTemplate, useUpdateBundle } from '../Access/hooks';
import { CAPABILITY_GROUPS, VIEW_MODE_LEGEND } from './vocabulary';
// no_view is the no-bundle fallback; a role defaulting to it would be inert,
// so the editor doesn't offer it (mirrors the server allowlist).
const EDITABLE_MODES = VIEW_MODE_LEGEND.filter((m)=>m.mode !== 'no_view');
function slugify(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[0-9]+/, '').slice(0, 64);
}
/**
 * SAD-948: a role of area capabilities alone (IT Admin, Company Settings
 * Admin) grants nothing to look at, so it carries no `read`. Every other role
 * keeps read as its floor.
 */ function isAreaOnly(actions) {
    const list = [
        ...actions
    ];
    return list.length > 0 && list.every((a)=>a.startsWith('area:'));
}
function seedActions(actions) {
    return isAreaOnly(actions) ? new Set(actions) : new Set([
        'read',
        ...actions
    ]);
}
export function RoleEditorDrawer({ open, onOpenChange, editor }) {
    const isEdit = editor.mode === 'edit';
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [keyTouched, setKeyTouched] = useState(false);
    const [description, setDescription] = useState('');
    const [viewMode, setViewMode] = useState('masked');
    const [actions, setActions] = useState(new Set([
        'read'
    ]));
    const [packs, setPacks] = useState(new Set());
    // Re-seed the form whenever the drawer opens for a new target.
    useEffect(()=>{
        if (!open) return;
        const init = editor.initial;
        setName(init?.displayName ?? '');
        setKey(init?.key ?? '');
        setKeyTouched(isEdit || !!init?.key);
        setDescription(init?.description ?? '');
        setViewMode(init?.defaultViewMode || 'masked');
        setActions(seedActions(init?.permittedActions ?? []));
        setPacks(new Set(init?.dataPacks ?? []));
    }, [
        open,
        editor,
        isEdit
    ]);
    const create = useCreateBundle();
    const update = useUpdateBundle();
    const saveAsTemplate = useSaveAsTemplate();
    const pending = create.isPending || update.isPending;
    // Starting points come from the DB template gallery (platform + this
    // tenant's published private families) — the hardcoded array is gone.
    // Curated (recommended) picks surface first.
    const { data: roleTemplates } = useListRoleTemplates('published');
    const { data: dataPacks } = useListDataPacks();
    const templateChips = [
        ...roleTemplates ?? []
    ].sort((a, b)=>Number(b.recommended) - Number(a.recommended));
    const toggleAction = (a)=>{
        setActions((prev)=>{
            const next = new Set(prev);
            if (next.has(a)) next.delete(a);
            else next.add(a);
            // read is the floor — a role that cannot see cannot act — except for an
            // area-only role (SAD-948), which stays area-only while it is one.
            if (prev.has('read') || !isAreaOnly(next)) next.add('read');
            return next;
        });
    };
    const canSave = name.trim().length > 0 && key.trim().length > 1 && !pending;
    const save = async ()=>{
        const fields = {
            key: key.trim(),
            displayName: name.trim(),
            description: description.trim(),
            defaultViewMode: viewMode,
            permittedActions: [
                ...actions
            ].sort(),
            dataPacks: [
                ...packs
            ].sort()
        };
        // The hooks toast the failure; swallow the rejection so a server refusal
        // (e.g. duplicate key) keeps the drawer open instead of surfacing as an
        // uncaught error.
        try {
            if (isEdit) await update.mutateAsync(fields);
            else await create.mutateAsync(fields);
        } catch  {
            return;
        }
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(Sheet, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(SheetContent, {
            side: "right",
            className: "flex w-full flex-col gap-4 overflow-y-auto sm:max-w-md",
            "data-testid": Testids.adminConsole.roleEditorDrawer,
            children: [
                /*#__PURE__*/ _jsx(SheetHeader, {
                    children: /*#__PURE__*/ _jsx(SheetTitle, {
                        children: isEdit ? 'Edit role' : 'Create custom role'
                    })
                }),
                !isEdit && templateChips.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            children: "Start from a template (optional)"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-wrap gap-1.5",
                            children: templateChips.map((t)=>/*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    "data-testid": Testids.adminConsole.roleEditorTemplate,
                                    "data-template": t.displayName,
                                    className: "rounded-full border px-2.5 py-0.5 text-xs hover:bg-muted",
                                    title: t.description,
                                    onClick: ()=>{
                                        setName(t.displayName);
                                        setKey(slugify(t.displayName));
                                        setKeyTouched(false);
                                        setDescription(t.description);
                                        setViewMode(t.defaultViewMode);
                                        setActions(seedActions(t.permittedActions));
                                        setPacks(new Set(t.dataPacks));
                                    },
                                    children: [
                                        t.displayName,
                                        t.recommended ? ' ★' : '',
                                        !t.isPlatform ? ' · yours' : ''
                                    ]
                                }, t.id))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "role-name",
                            children: "Name"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            id: "role-name",
                            "data-testid": Testids.adminConsole.roleEditorName,
                            value: name,
                            placeholder: "e.g. Proposal Reviewer",
                            onChange: (e)=>{
                                setName(e.target.value);
                                if (!keyTouched) setKey(slugify(e.target.value));
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "role-key",
                            children: "Key"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            id: "role-key",
                            value: key,
                            disabled: isEdit,
                            className: "font-mono text-xs",
                            onChange: (e)=>{
                                setKeyTouched(true);
                                setKey(slugify(e.target.value));
                            }
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "text-[11px] text-muted-foreground",
                            children: [
                                "Stable identifier used by grants",
                                isEdit ? ' — cannot change on an existing role' : '',
                                "."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "role-description",
                            children: "Description"
                        }),
                        /*#__PURE__*/ _jsx(Textarea, {
                            id: "role-description",
                            "data-testid": Testids.adminConsole.roleEditorDescription,
                            value: description,
                            placeholder: "What this role is for — shown in the matrix and grant dialogs",
                            onChange: (e)=>setDescription(e.target.value)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            children: "What they can see"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "grid gap-1.5",
                            children: EDITABLE_MODES.map((m)=>/*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    "data-testid": Testids.adminConsole.roleEditorViewMode,
                                    "data-view-mode": m.mode,
                                    onClick: ()=>setViewMode(m.mode),
                                    className: `rounded-md border px-3 py-2 text-left text-sm transition-colors ${viewMode === m.mode ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40'}`,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `rounded px-1.5 py-0.5 text-xs font-medium ${m.className}`,
                                            children: m.label
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "ml-2 text-xs text-muted-foreground",
                                            children: m.meaning
                                        })
                                    ]
                                }, m.mode))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            children: "What they can do"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-[11px] text-muted-foreground",
                            children: "Seeing content is always included; everything else is opt-in."
                        }),
                        (dataPacks ?? []).length > 0 && /*#__PURE__*/ _jsxs("fieldset", {
                            className: "space-y-1 rounded-md border p-2",
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                                    children: "Sensitive data"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "px-1 text-[11px] text-muted-foreground",
                                    children: "Numbers in these packs show unmasked to holders of this role, even at the Masked level."
                                }),
                                (dataPacks ?? []).map((pack)=>/*#__PURE__*/ _jsxs("label", {
                                        className: "flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-muted/30",
                                        children: [
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "checkbox",
                                                "data-testid": Testids.adminConsole.roleEditorDataPack,
                                                "data-pack": pack.key,
                                                className: "mt-0.5",
                                                checked: packs.has(pack.key),
                                                onChange: ()=>setPacks((prev)=>{
                                                        const next = new Set(prev);
                                                        if (next.has(pack.key)) next.delete(pack.key);
                                                        else next.add(pack.key);
                                                        return next;
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-sm font-medium",
                                                        children: pack.displayName
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "block text-xs text-muted-foreground",
                                                        children: pack.description
                                                    })
                                                ]
                                            })
                                        ]
                                    }, pack.key))
                            ]
                        }),
                        CAPABILITY_GROUPS.map((g)=>/*#__PURE__*/ _jsxs("fieldset", {
                                className: "space-y-1 rounded-md border p-2",
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        className: "px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                                        children: g.group
                                    }),
                                    g.items.map((cap)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-muted/30",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    "data-testid": Testids.adminConsole.roleEditorAction,
                                                    "data-action": cap.action,
                                                    className: "mt-0.5",
                                                    checked: actions.has(cap.action),
                                                    onChange: ()=>toggleAction(cap.action)
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium",
                                                            children: cap.label
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "block text-xs text-muted-foreground",
                                                            children: cap.description
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, cap.action))
                                ]
                            }, g.group))
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-auto flex items-center gap-2 border-t pt-3",
                    children: [
                        isEdit && /*#__PURE__*/ _jsx(Button, {
                            variant: "ghost",
                            className: "mr-auto text-xs",
                            "data-testid": Testids.adminConsole.roleEditorSaveAsTemplate,
                            disabled: saveAsTemplate.isPending,
                            title: "Snapshot this role as a private template draft — publish it to reuse as a starting point",
                            onClick: ()=>saveAsTemplate.mutate({
                                    bundleKey: key
                                }),
                            children: "Save as private template"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            className: "ml-auto",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminConsole.roleEditorSave,
                            disabled: !canSave,
                            onClick: save,
                            children: isEdit ? 'Save changes' : 'Create role'
                        })
                    ]
                })
            ]
        })
    });
}
